import * as React from 'react';
import { FATURA_ADIMI, type FaturaDurum, type FaturaTipi } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { Kart, KartBaslik, KartGovde } from '@/components/ui/card';
import { Alan } from '@/components/ui/field';
import { ApiHatasi, api } from '@/lib/api';
import { paraFormatla, tarihFormatla } from '@/lib/utils';

/**
 * e-Fatura / e-Arsiv ekrani.
 *
 * Tasarim kaynagi: Claude Design - "otelcms Fatura.dc.html".
 * Tasarimdaki fatura listesi, API gunlugu ve VKN sorgusu ureticiydi;
 * buradaki her sey gercek uclardan gelir:
 *   GET  /api/faturalar                  -> liste + KPI
 *   GET  /api/faturalar/faturalanabilir  -> faturasi kesilmemis kapali folyolar
 *   POST /api/faturalar                  -> Parasut'e yaz + GIB'e gonder
 *   POST /api/faturalar/:id/gonder       -> yeniden gonder
 *   GET  /api/faturalar/vkn/:vkn         -> e-Fatura mukellefi mi
 *   GET  /api/faturalar/sistem/gunluk    -> API gunlugu
 *
 * Tasarimdaki dort adimli akis serisi burada da var; farki, adimin
 * gercekten nerede oldugunu Parasut'un `trackable_job` sonucu belirliyor.
 */

interface Fatura {
  id: string;
  faturaNo: string | null;
  tip: FaturaTipi;
  durum: FaturaDurum;
  tutarKurus: number;
  aliciAd: string;
  aliciVergiNo: string | null;
  aliciTip: 'SAHIS' | 'SIRKET';
  hataMesaji: string | null;
  ettn: string | null;
  parasutInvoiceId: string | null;
  odaNo: string | null;
  an: number;
}

interface Ozet {
  onayliToplamKurus: number;
  onayli: number;
  bekleyen: number;
  hatali: number;
  toplam: number;
}

interface Folyo {
  folioId: string;
  toplamKurus: number;
  odaNo: string | null;
  girisTarihi: string;
  cikisTarihi: string;
  misafirAd: string;
}

interface GunlukSatir {
  id: string;
  yontem: string;
  yol: string;
  durumKodu: number;
  sureMs: number;
  hata: string | null;
  an: number;
}

const DURUM: Record<FaturaDurum, { etiket: string; yazi: string; zemin: string }> = {
  TASLAK: { etiket: 'Taslak', yazi: '#8a93ad', zemin: '#eef1f8' },
  GONDERILDI: { etiket: "GİB'de", yazi: '#1f4fd8', zemin: '#edf2ff' },
  ONAYLANDI: { etiket: 'GİB onaylı', yazi: '#12a150', zemin: '#e6f7ec' },
  HATA: { etiket: 'Hata', yazi: '#e5484d', zemin: '#fdeceb' },
  IPTAL: { etiket: 'İptal', yazi: '#8a93ad', zemin: '#eef1f8' },
};

const TIP: Record<FaturaTipi, { etiket: string; yazi: string; zemin: string }> = {
  E_FATURA: { etiket: 'e-Fatura', yazi: '#0b1530', zemin: '#dce5ff' },
  E_ARSIV: { etiket: 'e-Arşiv', yazi: '#7c3aed', zemin: '#f3e8ff' },
  MATBU: { etiket: 'Matbu', yazi: '#5b6480', zemin: '#eef1f8' },
};

const saat = (an: number) => new Date(an).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

export function Faturalar({ yetkiler }: { yetkiler: string[] }) {
  const yazabilir = yetkiler.includes('invoice:write');
  const ayarYetkisi = yetkiler.includes('tenant:settings');

  const [sekme, setSekme] = React.useState<'liste' | 'ayarlar' | 'gunluk'>('liste');
  const [faturalar, setFaturalar] = React.useState<Fatura[]>([]);
  const [ozet, setOzet] = React.useState<Ozet | null>(null);
  const [suzgec, setSuzgec] = React.useState<'hepsi' | FaturaDurum>('hepsi');
  const [seciliId, setSeciliId] = React.useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [islenen, setIslenen] = React.useState<string | null>(null);
  const [yeniAcik, setYeniAcik] = React.useState(false);

  const yukle = React.useCallback(async () => {
    try {
      const v = await api<{ faturalar: Fatura[]; ozet: Ozet }>('/api/faturalar');
      setFaturalar(v.faturalar);
      setOzet(v.ozet);
      setSeciliId((o) => o ?? v.faturalar[0]?.id ?? null);
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Faturalar alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  /*
   * GIB sonucu asenkron gelir. Saatlik nobetci zaten yokluyor; ekran acikken
   * 30 saniyede bir tazelemek "gonderildi" satirinin donup kalmasini onler.
   */
  React.useEffect(() => {
    const bekleyenVar = faturalar.some((f) => f.durum === 'GONDERILDI');
    if (!bekleyenVar) return;
    const z = setInterval(() => {
      if (document.visibilityState === 'visible') void yukle();
    }, 30_000);
    return () => clearInterval(z);
  }, [faturalar, yukle]);

  async function gonder(f: Fatura) {
    if (!yazabilir || islenen) return;
    setIslenen(f.id);
    setHata(null);
    try {
      await api(`/api/faturalar/${f.id}/gonder`, { method: 'POST' });
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Gönderilemedi.');
    } finally {
      setIslenen(null);
    }
  }

  const gosterilen = suzgec === 'hepsi' ? faturalar : faturalar.filter((f) => f.durum === suzgec);
  const secili = faturalar.find((f) => f.id === seciliId) ?? null;
  const say = (d: FaturaDurum) => faturalar.filter((f) => f.durum === d).length;

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-3.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-3xl font-bold tracking-[-.03em]">e-Fatura</h1>

        <div className="flex w-full gap-1 rounded-full border border-cizgi bg-white p-1 sm:w-auto overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {(
            [
              ['liste', 'Faturalar', say('HATA') + say('TASLAK')],
              ...(ayarYetkisi ? ([['ayarlar', 'Ayarlar', 0]] as const) : []),
              ['gunluk', 'API günlüğü', 0],
            ] as const
          ).map(([k, etiket, rozet]) => (
            <button
              key={k}
              onClick={() => setSekme(k as typeof sekme)}
              className={
                'flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-[7px] text-[13px] font-semibold transition-colors ' +
                (sekme === k ? 'bg-lacivert text-white' : 'text-gri hover:text-lacivert')
              }
            >
              {etiket}
              {rozet > 0 && (
                <span className="grid min-w-5 place-items-center rounded-full bg-sari px-1.5 text-[11px] font-bold text-lacivert">
                  {rozet}
                </span>
              )}
            </button>
          ))}
        </div>

        {sekme === 'liste' && yazabilir && (
          <Button className="ml-auto" onClick={() => setYeniAcik(true)}>
            + Yeni fatura
          </Button>
        )}
      </div>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}

      {sekme === 'ayarlar' ? (
        <ParasutAyarlari />
      ) : sekme === 'gunluk' ? (
        <ApiGunlugu />
      ) : (
        <>
          {ozet && (
            <div className="grid gap-3 [animation:rise_.6s_.05s_cubic-bezier(.2,.7,.2,1)_both] sm:grid-cols-2 xl:grid-cols-4">
              <Kpi etiket="Faturalanan" deger={paraFormatla(ozet.onayliToplamKurus)} alt={`${ozet.onayli} onaylı fatura`} />
              <Kpi etiket="Bekleyen" deger={String(ozet.bekleyen)} alt="taslak + GİB'de" renk="#1f4fd8" />
              <Kpi
                etiket="Hatalı"
                deger={String(ozet.hatali)}
                alt={ozet.hatali ? 'müdahale gerekli' : 'sorun yok'}
                renk={ozet.hatali ? '#e5484d' : '#12a150'}
              />
              <Kpi etiket="Toplam" deger={String(ozet.toplam)} alt="tüm kayıtlar" />
            </div>
          )}

          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ['hepsi', 'Tümü', faturalar.length],
                ['ONAYLANDI', 'Onaylı', say('ONAYLANDI')],
                ['GONDERILDI', "GİB'de", say('GONDERILDI')],
                ['TASLAK', 'Taslak', say('TASLAK')],
                ['HATA', 'Hatalı', say('HATA')],
              ] as const
            ).map(([k, etiket, n]) => (
              <button
                key={k}
                onClick={() => setSuzgec(k as typeof suzgec)}
                className={
                  'flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors ' +
                  (suzgec === k ? 'border-lacivert bg-lacivert text-white' : 'border-cizgi bg-white text-gri')
                }
              >
                {etiket}
                <span className={suzgec === k ? 'opacity-70' : 'text-[#8a93ad]'}>{n}</span>
              </button>
            ))}
          </div>

          <div className="grid items-start gap-[18px] xl:grid-cols-[minmax(0,1fr)_360px]">
            <Kart>
              <KartGovde className="overflow-x-auto p-0">
                {yukleniyor ? (
                  <div className="p-5">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="mb-2 h-14 animate-pulse rounded-xl bg-satir" />
                    ))}
                  </div>
                ) : gosterilen.length === 0 ? (
                  <p className="p-10 text-center text-[13px] text-gri">
                    {faturalar.length === 0
                      ? 'Henüz fatura kesilmemiş. Kapanmış bir folyodan “Yeni fatura” ile başlayın.'
                      : 'Bu süzgeçte fatura yok.'}
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-slate-600">
                      <tr>
                        <th className="p-3">Fatura</th>
                        <th className="p-3">Alıcı</th>
                        <th className="p-3">Tür</th>
                        <th className="p-3">Durum</th>
                        <th className="p-3 text-right">Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gosterilen.map((f) => (
                        <tr
                          key={f.id}
                          onClick={() => setSeciliId(f.id)}
                          className={
                            'cursor-pointer border-t border-slate-100 transition-colors ' +
                            (seciliId === f.id ? 'bg-mavi-zemin' : 'hover:bg-zemin')
                          }
                        >
                          <td className="p-3">
                            <div className="font-semibold text-lacivert">{f.faturaNo ?? '—'}</div>
                            <div className="text-xs text-gri">
                              {f.odaNo ? `Oda ${f.odaNo} · ` : ''}
                              {tarihFormatla(new Date(f.an).toISOString())}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="font-medium text-lacivert">{f.aliciAd}</div>
                            <div className="text-xs text-gri">
                              {f.aliciTip === 'SIRKET' ? 'VKN' : 'Şahıs'} {f.aliciVergiNo ?? ''}
                            </div>
                          </td>
                          <td className="p-3">
                            <span
                              className="rounded-full px-2.5 py-1 text-[11px] font-bold"
                              style={{ background: TIP[f.tip].zemin, color: TIP[f.tip].yazi }}
                            >
                              {TIP[f.tip].etiket}
                            </span>
                          </td>
                          <td className="p-3">
                            <span
                              className="flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
                              style={{ background: DURUM[f.durum].zemin, color: DURUM[f.durum].yazi }}
                            >
                              {f.durum === 'GONDERILDI' && (
                                <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" className="[animation:spin_.9s_linear_infinite]" aria-hidden="true">
                                  <path d="M7 1.5a5.5 5.5 0 1 1-5.5 5.5" />
                                </svg>
                              )}
                              {DURUM[f.durum].etiket}
                            </span>
                          </td>
                          <td className="p-3 text-right font-semibold tabular-nums">
                            {paraFormatla(f.tutarKurus)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </KartGovde>
            </Kart>

            {secili && (
              <FaturaDetay
                fatura={secili}
                yazabilir={yazabilir}
                islenen={islenen === secili.id}
                onGonder={() => void gonder(secili)}
              />
            )}
          </div>
        </>
      )}

      {yeniAcik && (
        <YeniFatura
          onKapat={() => setYeniAcik(false)}
          onOlusturuldu={async () => {
            setYeniAcik(false);
            await yukle();
          }}
        />
      )}
    </main>
  );
}

function Kpi({ etiket, deger, alt, renk }: { etiket: string; deger: string; alt: string; renk?: string }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-2xl border border-cizgi bg-white p-4">
      <span className="text-xs font-semibold uppercase tracking-[.08em] text-gri">{etiket}</span>
      <span className="text-[26px] font-bold leading-none tracking-[-.03em]" style={renk ? { color: renk } : undefined}>
        {deger}
      </span>
      <span className="text-xs text-gri">{alt}</span>
    </div>
  );
}

/** Tasarimdaki dort adimli akis serisi + hata kutusu. */
function FaturaDetay({
  fatura,
  yazabilir,
  islenen,
  onGonder,
}: {
  fatura: Fatura;
  yazabilir: boolean;
  islenen: boolean;
  onGonder: () => void;
}) {
  const adim = FATURA_ADIMI[fatura.durum];
  const hataliAdim = fatura.durum === 'HATA';

  const adimlar = [
    { baslik: 'Folio kapandı', alt: fatura.odaNo ? `check-out · oda ${fatura.odaNo}` : 'check-out' },
    { baslik: "Paraşüt'e yazıldı", alt: fatura.parasutInvoiceId ? `sales_invoices/${fatura.parasutInvoiceId}` : '—' },
    {
      baslik: "GİB'e gönderildi",
      alt: fatura.tip === 'E_FATURA' ? 'e_invoice · mükellef' : 'e_archive · şahıs',
    },
    { baslik: 'Onaylandı', alt: fatura.ettn ? `ETTN ${fatura.ettn.slice(0, 8)}…` : 'bekliyor' },
  ];

  return (
    <Kart className="[animation:rise_.6s_.15s_cubic-bezier(.2,.7,.2,1)_both] xl:sticky xl:top-[92px]">
      <KartBaslik>
        <div className="flex flex-col gap-0.5">
          <h2 className="text-[17px] font-bold tracking-[-.01em]">{fatura.faturaNo ?? 'Taslak fatura'}</h2>
          <span className="text-xs text-gri">{fatura.aliciAd}</span>
        </div>
        <span
          className="ml-auto rounded-full px-2.5 py-1 text-[11px] font-bold"
          style={{ background: DURUM[fatura.durum].zemin, color: DURUM[fatura.durum].yazi }}
        >
          {DURUM[fatura.durum].etiket}
        </span>
      </KartBaslik>
      <KartGovde className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between">
          <span className="text-[13px] text-gri">Toplam</span>
          <span className="text-2xl font-bold tabular-nums tracking-[-.02em]">
            {paraFormatla(fatura.tutarKurus)}
          </span>
        </div>

        <div className="flex flex-col gap-0">
          {adimlar.map((a, i) => {
            const tamam = i < adim || (i === adim && fatura.durum === 'ONAYLANDI');
            const bekliyor = i === adim && fatura.durum === 'GONDERILDI';
            const hatali = hataliAdim && i === 2;
            return (
              <div key={a.baslik} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className="grid size-6 flex-none place-items-center rounded-full text-white"
                    style={{
                      background: hatali ? '#e5484d' : tamam ? '#12a150' : bekliyor ? '#1f4fd8' : '#dfe6f5',
                    }}
                  >
                    {hatali ? (
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <path d="M3 3l6 6M9 3l-6 6" />
                      </svg>
                    ) : tamam ? (
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M2.5 6l2.5 2.5 4.5-5" />
                      </svg>
                    ) : bekliyor ? (
                      <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth="2.2" className="[animation:spin_.9s_linear_infinite]" aria-hidden="true">
                        <path d="M7 1.5a5.5 5.5 0 1 1-5.5 5.5" />
                      </svg>
                    ) : null}
                  </span>
                  {i < 3 && <span className="w-0.5 flex-1 bg-cizgi" />}
                </div>
                <div className="flex flex-col pb-4">
                  <span className={'text-sm font-semibold ' + (tamam || bekliyor || hatali ? 'text-lacivert' : 'text-[#8a93ad]')}>
                    {a.baslik}
                  </span>
                  <span className="text-xs text-gri">{a.alt}</span>
                </div>
              </div>
            );
          })}
        </div>

        {fatura.hataMesaji && (
          <div className="rounded-xl border border-red-300 bg-red-50 px-3.5 py-3 text-[13px] leading-relaxed text-red-800">
            {fatura.hataMesaji}
          </div>
        )}

        {yazabilir && (fatura.durum === 'TASLAK' || fatura.durum === 'HATA') && (
          <Button onClick={onGonder} yukleniyor={islenen} boyut="lg">
            {fatura.durum === 'HATA' ? 'Yeniden gönder' : "GİB'e gönder"}
          </Button>
        )}
      </KartGovde>
    </Kart>
  );
}

/** Kapanmis folyodan fatura kesme penceresi (tasarimdaki "yeni fatura"). */
function YeniFatura({ onKapat, onOlusturuldu }: { onKapat: () => void; onOlusturuldu: () => void | Promise<void> }) {
  const [folyolar, setFolyolar] = React.useState<Folyo[]>([]);
  const [secili, setSecili] = React.useState<string | null>(null);
  const [aliciTip, setAliciTip] = React.useState<'SAHIS' | 'SIRKET'>('SAHIS');
  const [unvan, setUnvan] = React.useState('');
  const [vkn, setVkn] = React.useState('');
  const [vergiDairesi, setVergiDairesi] = React.useState('');
  const [sorgu, setSorgu] = React.useState<{ mukellef: boolean; ad: string | null } | null>(null);
  const [sorguluyor, setSorguluyor] = React.useState(false);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  React.useEffect(() => {
    void api<{ folyolar: Folyo[] }>('/api/faturalar/faturalanabilir')
      .then((v) => {
        setFolyolar(v.folyolar);
        setSecili(v.folyolar[0]?.folioId ?? null);
      })
      .catch((e) => setHata(e instanceof ApiHatasi ? e.message : 'Folyolar alınamadı.'));
  }, []);

  async function vknSorgula() {
    if (vkn.length !== 10 || sorguluyor) return;
    setSorguluyor(true);
    setHata(null);
    try {
      const v = await api<{ mukellef: boolean; ad: string | null }>(`/api/faturalar/vkn/${vkn}`);
      setSorgu(v);
      if (v.ad && !unvan) setUnvan(v.ad);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'VKN sorgulanamadı.');
    } finally {
      setSorguluyor(false);
    }
  }

  async function olustur(e: React.FormEvent) {
    e.preventDefault();
    if (!secili || gonderiliyor) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      await api('/api/faturalar', {
        method: 'POST',
        body: JSON.stringify({
          folioId: secili,
          aliciTip,
          ...(aliciTip === 'SIRKET'
            ? { unvan: unvan.trim(), vergiNo: vkn, vergiDairesi: vergiDairesi.trim() || undefined }
            : {}),
        }),
      });
      await onOlusturuldu();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Fatura oluşturulamadı.');
      setGonderiliyor(false);
    }
  }

  const tur = aliciTip === 'SIRKET' ? (sorgu ? (sorgu.mukellef ? 'e-Fatura' : 'e-Arşiv') : 'VKN sorgulanacak') : 'e-Arşiv';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-lacivert/60 p-4" onClick={onKapat} role="presentation">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={olustur}
        className="flex max-h-[90dvh] w-full max-w-[560px] flex-col gap-4 overflow-y-auto rounded-[20px] bg-white p-6 [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
      >
        <div>
          <h2 className="text-xl font-bold">Yeni fatura</h2>
          <p className="mt-1 text-sm text-gri">Kapanmış bir folyodan e-Fatura ya da e-Arşiv kesilir.</p>
        </div>

        {folyolar.length === 0 ? (
          <p className="rounded-xl bg-zemin px-4 py-3 text-[13px] text-gri">
            Faturalanacak kapalı folyo yok. Check-out yapılan konaklamalar burada listelenir.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-slate-700">Folyo</span>
            {folyolar.map((f) => (
              <button
                key={f.folioId}
                type="button"
                onClick={() => setSecili(f.folioId)}
                className={
                  'flex items-center gap-3 rounded-xl border-[1.5px] p-3 text-left transition-colors ' +
                  (secili === f.folioId ? 'border-marka-600 bg-mavi-zemin' : 'border-cizgi bg-white')
                }
              >
                <span className="grid size-10 flex-none place-items-center rounded-xl bg-zemin text-sm font-bold">
                  {f.odaNo ?? '—'}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-semibold text-lacivert">{f.misafirAd}</span>
                  <span className="text-xs text-gri">
                    {tarihFormatla(f.girisTarihi)} – {tarihFormatla(f.cikisTarihi)}
                  </span>
                </span>
                <span className="flex-none font-semibold tabular-nums">{paraFormatla(f.toplamKurus)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-slate-700">Alıcı</span>
          <div className="flex gap-2">
            {(
              [
                ['SAHIS', 'Şahıs (misafir)'],
                ['SIRKET', 'Şirket'],
              ] as const
            ).map(([k, etiket]) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setAliciTip(k);
                  setSorgu(null);
                }}
                className={
                  'flex-1 rounded-xl border-[1.5px] px-3 py-2.5 text-[13px] font-semibold transition-colors ' +
                  (aliciTip === k ? 'border-marka-600 bg-mavi-zemin text-lacivert' : 'border-cizgi bg-white text-gri')
                }
              >
                {etiket}
              </button>
            ))}
          </div>
        </div>

        {aliciTip === 'SIRKET' && (
          <>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Alan
                  etiket="VKN"
                  value={vkn}
                  onChange={(e) => {
                    setVkn(e.target.value.replace(/\D/g, ''));
                    setSorgu(null);
                  }}
                  inputMode="numeric"
                  maxLength={10}
                  ipucu="GİB mükellefliği sorgulanır; e-Fatura mı e-Arşiv mi buna göre belirlenir."
                />
              </div>
              <Button
                type="button"
                varyant="ikincil"
                onClick={() => void vknSorgula()}
                disabled={vkn.length !== 10}
                yukleniyor={sorguluyor}
              >
                Sorgula
              </Button>
            </div>

            {sorgu && (
              <div
                className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 text-[13px]"
                style={{ background: sorgu.mukellef ? '#dce5ff' : '#f3e8ff' }}
              >
                <span
                  className="size-2 flex-none rounded-full"
                  style={{ background: sorgu.mukellef ? '#0b1530' : '#7c3aed' }}
                />
                <span className="text-lacivert">
                  {sorgu.ad ? <strong>{sorgu.ad}</strong> : 'Kayıt bulunamadı'}
                  {' · '}
                  {sorgu.mukellef ? 'e-Fatura mükellefi' : 'Mükellef değil, e-Arşiv kesilecek'}
                </span>
              </div>
            )}

            <Alan etiket="Unvan" value={unvan} onChange={(e) => setUnvan(e.target.value)} maxLength={200} />
            <Alan
              etiket="Vergi dairesi"
              value={vergiDairesi}
              onChange={(e) => setVergiDairesi(e.target.value)}
              maxLength={100}
            />
          </>
        )}

        <div className="flex items-center gap-2 rounded-xl bg-zemin px-3.5 py-3 text-[13px] text-gri">
          Kesilecek belge: <strong className="text-lacivert">{tur}</strong>
        </div>

        {hata && <p className="text-sm text-red-600">{hata}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" varyant="sessiz" onClick={onKapat}>
            Vazgeç
          </Button>
          <Button
            type="submit"
            yukleniyor={gonderiliyor}
            disabled={!secili || (aliciTip === 'SIRKET' && (vkn.length !== 10 || unvan.trim().length < 2))}
          >
            Oluştur ve gönder
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Tasarimdaki "Ayarlar" sekmesi. */
function ParasutAyarlari() {
  const [ayar, setAyar] = React.useState<{
    parasutAcik: boolean;
    parasutFirmaNo: string | null;
    parasutOtomatikFatura: boolean;
    faturaSeri: string | null;
    kimlikTanimli: boolean;
  } | null>(null);
  const [acik, setAcik] = React.useState(false);
  const [otomatik, setOtomatik] = React.useState(false);
  const [firmaNo, setFirmaNo] = React.useState('');
  const [seri, setSeri] = React.useState('');
  const [clientId, setClientId] = React.useState('');
  const [clientSecret, setClientSecret] = React.useState('');
  const [refreshToken, setRefreshToken] = React.useState('');
  const [kaydediliyor, setKaydediliyor] = React.useState(false);
  const [test, setTest] = React.useState<string | null>(null);
  const [testEdiliyor, setTestEdiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  const yukle = React.useCallback(async () => {
    const v = await api<NonNullable<typeof ayar>>('/api/faturalar/sistem/ayarlar').catch(() => null);
    if (!v) return;
    setAyar(v);
    setAcik(v.parasutAcik);
    setOtomatik(v.parasutOtomatikFatura);
    setFirmaNo(v.parasutFirmaNo ?? '');
    setSeri(v.faturaSeri ?? '');
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    setKaydediliyor(true);
    setHata(null);
    try {
      await api('/api/faturalar/sistem/ayarlar', {
        method: 'PATCH',
        body: JSON.stringify({
          parasutAcik: acik,
          parasutOtomatikFatura: otomatik,
          parasutFirmaNo: firmaNo.trim() || null,
          faturaSeri: seri.trim().toUpperCase() || null,
          ...(clientId.trim() ? { clientId: clientId.trim() } : {}),
          ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}),
          ...(refreshToken.trim() ? { refreshToken: refreshToken.trim() } : {}),
        }),
      });
      setClientId('');
      setClientSecret('');
      setRefreshToken('');
      await yukle();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Kaydedilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  }

  async function baglantiTest() {
    setTestEdiliyor(true);
    setTest(null);
    try {
      const v = await api<{ ad: string | null }>('/api/faturalar/sistem/baglanti-testi', { method: 'POST' });
      setTest(`Bağlantı çalışıyor${v.ad ? ` · ${v.ad}` : ''}`);
    } catch (e) {
      setTest(e instanceof ApiHatasi ? e.message : 'Bağlantı kurulamadı.');
    } finally {
      setTestEdiliyor(false);
    }
  }

  if (!ayar) return <p className="text-sm text-gri">Yükleniyor…</p>;

  return (
    <form onSubmit={kaydet} className="grid items-start gap-[18px] xl:grid-cols-[minmax(0,480px)_minmax(0,1fr)]">
      <Kart>
        <KartBaslik>
          <h2 className="text-[17px] font-bold tracking-[-.01em]">Paraşüt bağlantısı</h2>
        </KartBaslik>
        <KartGovde className="flex flex-col gap-4">
          <label className="flex items-start gap-3">
            <input type="checkbox" checked={acik} onChange={(e) => setAcik(e.target.checked)} className="mt-1 size-4" />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-lacivert">e-Fatura entegrasyonunu aç</span>
              <span className="text-[13px] leading-relaxed text-gri">
                Kapalıyken fatura ekranı çalışır ama Paraşüt'e hiçbir istek gitmez.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={otomatik}
              onChange={(e) => setOtomatik(e.target.checked)}
              className="mt-1 size-4"
            />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-lacivert">Check-out'ta otomatik fatura</span>
              <span className="text-[13px] leading-relaxed text-gri">
                Folyo kapanınca şahıs faturası kendiliğinden kesilip GİB'e gönderilir. Şirket faturası VKN
                gerektirdiği için elle kesilir.
              </span>
            </span>
          </label>

          <Alan
            etiket="Paraşüt firma no"
            value={firmaNo}
            onChange={(e) => setFirmaNo(e.target.value.replace(/[^0-9_]/g, ''))}
            maxLength={20}
            ipucu="API yolunun parçasıdır: /v4/<firma no>/sales_invoices"
          />
          <Alan
            etiket="Fatura seri öneki"
            value={seri}
            onChange={(e) => setSeri(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
            maxLength={8}
            ipucu="ALA → ALA2026000117"
          />

          <div className="rounded-xl bg-zemin px-3.5 py-3 text-[13px] leading-relaxed text-gri">
            {ayar.kimlikTanimli ? (
              <>
                API kimlik bilgileri <strong className="text-lacivert">kayıtlı</strong>. Değiştirmek için aşağıdaki
                alanları doldurun; boş bırakılanlar korunur.
              </>
            ) : (
              <>
                API kimlik bilgileri henüz girilmemiş. <code className="rounded bg-white px-1">destek@parasut.com</code>{' '}
                adresinden alınan CLIENT_ID / CLIENT_SECRET ve bir refresh token gerekir.
              </>
            )}
          </div>

          <Alan etiket="CLIENT_ID" value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" />
          <Alan
            etiket="CLIENT_SECRET"
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            autoComplete="new-password"
          />
          <Alan
            etiket="Refresh token"
            type="password"
            value={refreshToken}
            onChange={(e) => setRefreshToken(e.target.value)}
            autoComplete="new-password"
            ipucu="Şifrelenerek saklanır ve her yenilemede kendiliğinden değişir."
          />

          {hata && <p className="text-sm text-red-600">{hata}</p>}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" yukleniyor={kaydediliyor}>
              Kaydet
            </Button>
            <Button type="button" varyant="ikincil" onClick={() => void baglantiTest()} yukleniyor={testEdiliyor}>
              Bağlantıyı test et
            </Button>
            {test && <span className="text-[13px] font-semibold text-gri">{test}</span>}
          </div>
        </KartGovde>
      </Kart>

      <Kart>
        <KartBaslik>
          <h2 className="text-[17px] font-bold tracking-[-.01em]">Nasıl çalışır</h2>
        </KartBaslik>
        <KartGovde className="flex flex-col gap-3 text-[13px] leading-relaxed text-gri">
          <p>
            Folyo kapanınca fatura önce Paraşüt'e yazılır (<code>contacts</code> + <code>sales_invoices</code>), sonra
            alıcının türüne göre <code>e_invoices</code> ya da <code>e_archives</code> ucuna gönderilir.
          </p>
          <p>
            GİB gönderimi <strong className="text-lacivert">asenkrondur</strong>: Paraşüt bir iş kimliği döner, kabul
            edilip edilmediği saatlik nöbetçi tarafından yoklanır. Bu yüzden fatura bir süre "GİB'de" görünebilir.
          </p>
          <p>
            Hatalı gönderim 15 dakika arayla en fazla 3 kez denenir. Eksik adres gibi kalıcı hatalar tekrar denemekle
            düzelmez; fatura listede "Hata" olarak bekler ve hata metni Paraşüt'ün kendi açıklamasıdır.
          </p>
          <p>
            Konaklama vergisi KDV matrahına girmediği için ayrı bir satır olarak, KDV oranı 0 ile yazılır. Oran hizmet tarihine göre seçilir (1 Mayıs 2026'dan beri %1).
          </p>
        </KartGovde>
      </Kart>
    </form>
  );
}

/** Tasarimdaki "API gunlugu" sekmesi. */
function ApiGunlugu() {
  const [gunluk, setGunluk] = React.useState<GunlukSatir[]>([]);
  const [yukleniyor, setYukleniyor] = React.useState(true);

  React.useEffect(() => {
    void api<{ gunluk: GunlukSatir[] }>('/api/faturalar/sistem/gunluk')
      .then((v) => setGunluk(v.gunluk))
      .catch(() => {})
      .finally(() => setYukleniyor(false));
  }, []);

  return (
    <Kart>
      <KartBaslik>
        <h2 className="text-[17px] font-bold tracking-[-.01em]">Paraşüt API günlüğü</h2>
        <span className="ml-auto text-[13px] text-gri">son 50 çağrı</span>
      </KartBaslik>
      <KartGovde className="p-0">
        {yukleniyor ? (
          <p className="p-6 text-[13px] text-gri">Yükleniyor…</p>
        ) : gunluk.length === 0 ? (
          <p className="p-10 text-center text-[13px] leading-relaxed text-gri">
            Henüz Paraşüt'e istek gitmemiş. Bağlantı ayarlandıktan sonra her çağrı burada görünür.
          </p>
        ) : (
          <div className="flex flex-col">
            {gunluk.map((l) => (
              <div key={l.id} className="flex items-center gap-3 border-t border-slate-100 px-4 py-2.5 first:border-t-0">
                <span className="w-11 flex-none tabular-nums text-xs text-gri">{saat(l.an)}</span>
                <span
                  className="w-12 flex-none rounded px-1.5 py-0.5 text-center text-[11px] font-bold"
                  style={{
                    background: l.yontem === 'GET' ? '#edf2ff' : '#e6f7ec',
                    color: l.yontem === 'GET' ? '#1f4fd8' : '#0f7a3d',
                  }}
                >
                  {l.yontem}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-lacivert">{l.yol}</span>
                <span
                  className="flex-none tabular-nums text-xs font-bold"
                  style={{ color: l.durumKodu >= 400 || l.durumKodu === 0 ? '#e5484d' : l.durumKodu >= 201 ? '#1f4fd8' : '#12a150' }}
                >
                  {l.durumKodu || 'ağ'}
                </span>
                <span className="w-14 flex-none text-right tabular-nums text-xs text-gri">{l.sureMs} ms</span>
              </div>
            ))}
          </div>
        )}
      </KartGovde>
    </Kart>
  );
}
